<template>
  <div>
    <div class="page-toolbar">
      <div class="page-title">小程序用户管理</div>
      <div style="display:flex; gap: 10px;">
        <el-input
          v-model.trim="keyword"
          placeholder="按名称/昵称/用户ID/公司/手机号筛选"
          clearable
          style="width: 260px;"
          @input="handleFilterChange"
        />
        <el-button icon="el-icon-refresh" @click="loadUsers">刷新</el-button>
      </div>
    </div>

    <el-card class="page-card">
      <el-table :data="pagedUsers" v-loading="loading" stripe>
        <el-table-column type="index" :index="paginationIndex" width="56" label="#" />
        <el-table-column label="用户" min-width="240">
          <template slot-scope="{ row }">
            <div class="user-cell">
              <el-avatar :size="34" :src="row.avatar || ''" icon="el-icon-user-solid" />
              <div>
                <div class="u-name">{{ formatDisplayName(row) }}</div>
                <div class="u-id">{{ row.companyName || '-' }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="用户ID" width="110">
          <template slot-scope="{ row }">
            <span>{{ formatUserCode(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="contactPhone" label="手机号" width="140" />
        <el-table-column label="资质状态" width="120">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="qualificationTagType(row.qualificationStatus)">
              {{ qualificationStatusLabel(row.qualificationStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="是否供应商" width="110">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.supplier ? 'success' : 'info'">
              {{ row.supplier ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="保证金余额" width="140">
          <template slot-scope="{ row }">
            <span :class="depositAmountClass(row)">{{ depositDisplayText(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="110">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.blacklisted ? 'danger' : 'success'">
              {{ row.blacklisted ? '已拉黑' : '正常' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="updatedAt" label="更新时间" width="170" />
        <el-table-column label="操作" width="500" fixed="right">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="viewUser(row)">查看</el-button>
            <el-button size="mini" type="primary" plain @click="editUser(row)">编辑资料</el-button>
            <el-button
              size="mini"
              type="primary"
              plain
              :disabled="publicationCount(row) === 0"
              @click="viewPublications(row)"
            >
              发布({{ publicationCount(row) }})
            </el-button>
            <el-button size="mini" type="warning" :disabled="isDepositUnavailable(row) || !Number(row.depositBalanceFen)" @click="handleDepositDeduct(row)">扣保证金</el-button>
            <el-button
              size="mini"
              :type="row.blacklisted ? 'success' : 'danger'"
              @click="toggleBlacklist(row)"
            >
              {{ row.blacklisted ? '取消拉黑' : '拉黑' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="list-pagination">
        <el-pagination
          background
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="pageSizes"
          :total="filteredUsers.length"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handlePageSizeChange"
          @current-change="handleCurrentPageChange"
        />
      </div>
    </el-card>

    <el-drawer
      title="用户详情"
      :visible.sync="drawerVisible"
      size="720px"
      direction="rtl"
    >
      <div v-if="currentUser" class="detail-wrap">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="用户ID">{{ formatUserCode(currentUser) }}</el-descriptions-item>
          <el-descriptions-item label="名称/昵称">{{ formatDisplayName(currentUser) }}</el-descriptions-item>
          <el-descriptions-item label="头像">
            <el-image
              v-if="currentUser.avatar"
              :src="currentUser.avatar"
              :preview-src-list="[currentUser.avatar]"
              fit="cover"
              class="detail-avatar"
            />
            <span v-else>-</span>
          </el-descriptions-item>
          <el-descriptions-item label="公司">{{ currentUser.companyName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ currentUser.contactPhone || '-' }}</el-descriptions-item>
          <el-descriptions-item label="资质状态">{{ qualificationStatusLabel(currentUser.qualificationStatus, '-') }}</el-descriptions-item>
          <el-descriptions-item label="供应商">{{ currentUser.supplier ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="保证金余额">{{ depositDisplayText(currentUser) }}</el-descriptions-item>
          <el-descriptions-item label="保证金规则">{{ currentUser.depositExempt ? '本轮豁免' : '按当前全局规则执行' }}</el-descriptions-item>
          <el-descriptions-item label="拉黑">{{ currentUser.blacklisted ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="最近登录">{{ currentUser.lastLoginTime || '-' }}</el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ currentUser.createdAt || '-' }}</el-descriptions-item>
          <el-descriptions-item label="更新时间">{{ currentUser.updatedAt || '-' }}</el-descriptions-item>
        </el-descriptions>
        <UserProfileChangeLogList ref="userChangeLogList" :openid="currentUser.openid" />
      </div>
    </el-drawer>

    <UserProfileQualificationEditDialog
      :visible.sync="editDialogVisible"
      :openid="editingOpenid"
      @saved="handleProfileSaved"
    />
  </div>
</template>

<script>
import { deductUserDeposit, fetchUserDetail, fetchUsers, setUserBlacklist } from '../../api/admin'
import { isAuthRedirectError } from '../../utils/auth'
import UserProfileChangeLogList from '../../components/UserProfileChangeLogList.vue'
import UserProfileQualificationEditDialog from '../../components/UserProfileQualificationEditDialog.vue'

export default {
  name: 'UserListView',
  components: {
    UserProfileChangeLogList,
    UserProfileQualificationEditDialog
  },
  data() {
    return {
      loading: false,
      users: [],
      keyword: '',
      currentPage: 1,
      pageSize: 10,
      pageSizes: [10, 20, 50, 100],
      drawerVisible: false,
      currentUser: null,
      editDialogVisible: false,
      editingOpenid: ''
    }
  },
  computed: {
    filteredUsers() {
      const key = String(this.keyword || '').toLowerCase()
      if (!key) return this.users
      return this.users.filter((u) => {
        const displayName = this.formatDisplayName(u).toLowerCase()
        const userCode = this.formatUserCode(u).toLowerCase()
        const companyName = String(u.companyName || '').toLowerCase()
        const contactPhone = String(u.contactPhone || '').toLowerCase()
        return displayName.includes(key) || userCode.includes(key) || companyName.includes(key) || contactPhone.includes(key)
      })
    },
    pagedUsers() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredUsers.slice(start, start + this.pageSize)
    }
  },
  created() {
    this.loadUsers()
  },
  methods: {
    formatDisplayName(user) {
      const nickName = String((user && (user.nickName || user.nickname)) || '').trim()
      if (nickName) return nickName
      const contactName = String((user && user.contactName) || '').trim()
      if (contactName) return contactName
      return this.formatUserCode(user)
    },
    publicationCount(user) {
      const count = Number(user && user.publicationCount)
      if (!Number.isFinite(count) || count <= 0) return 0
      return Math.floor(count)
    },
    viewPublications(user) {
      if (!user || !user.openid || this.publicationCount(user) === 0) return
      this.$router.push({
        name: 'user-publications',
        params: { openid: user.openid }
      })
    },
    formatUserCode(user) {
      const directCode = String((user && user.userCode) || '').trim()
      if (directCode) return directCode
      const userId = Number(user && user.userId)
      if (!Number.isInteger(userId) || userId <= 0) return '--'
      return userId < 1000 ? String(userId).padStart(3, '0') : String(userId)
    },
    qualificationTagType(status) {
      if (status === 'approved') return 'success'
      if (status === 'rejected') return 'danger'
      if (status === 'pending') return 'warning'
      return 'info'
    },
    qualificationStatusLabel(status, fallback = '未认证') {
      if (status === 'approved') return '已通过'
      if (status === 'rejected') return '已驳回'
      if (status === 'pending') return '待审核'
      if (status === 'none') return '未认证'
      return status || fallback
    },
    paginationIndex(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1
    },
    handleFilterChange() {
      this.currentPage = 1
    },
    handlePageSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
    },
    handleCurrentPageChange(page) {
      this.currentPage = page
    },
    adjustCurrentPage() {
      const maxPage = Math.max(1, Math.ceil(this.filteredUsers.length / this.pageSize))
      this.currentPage = Math.min(this.currentPage, maxPage)
    },
    formatAmountFen(amountFen) {
      const amount = Number(amountFen)
      if (!Number.isFinite(amount) || amount <= 0) {
        return '¥0.00'
      }
      return `¥${(amount / 100).toFixed(2)}`
    },
    isDepositDisabled(user) {
      return user && user.depositPaymentStatus === 'disabled'
    },
    isDepositUnavailable(user) {
      return this.isDepositDisabled(user) || !!(user && user.depositExempt)
    },
    depositDisplayText(user) {
      if (this.isDepositDisabled(user)) return '已关闭'
      if (user && user.depositExempt) return '保证金豁免'
      return this.formatAmountFen(user && user.depositBalanceFen)
    },
    depositAmountClass(user) {
      if (this.isDepositDisabled(user)) return 'deposit-amount-disabled'
      if (user && user.depositExempt) return 'deposit-amount-exempt'
      return Number(user && user.depositBalanceFen) > 0 ? 'deposit-amount-ok' : 'deposit-amount-empty'
    },
    async loadUsers() {
      this.loading = true
      try {
        this.users = await fetchUsers()
        this.adjustCurrentPage()
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '用户列表获取失败')
      } finally {
        this.loading = false
      }
    },
    async viewUser(row) {
      try {
        this.currentUser = await fetchUserDetail(row.openid)
        this.drawerVisible = true
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '用户详情获取失败')
      }
    },
    editUser(row) {
      if (!row || !row.openid) return
      this.editingOpenid = row.openid
      this.editDialogVisible = true
    },
    async handleProfileSaved(result) {
      const openid = this.editingOpenid
      await this.loadUsers()
      if (this.currentUser && this.currentUser.openid === openid) {
        this.currentUser = result && result.user ? result.user : await fetchUserDetail(openid)
        this.$nextTick(() => {
          if (this.$refs.userChangeLogList) {
            this.$refs.userChangeLogList.loadLogs()
          }
        })
      }
    },
    toggleBlacklist(row) {
      const action = row.blacklisted ? '取消拉黑' : '拉黑'
      this.$confirm(`确认${action}该用户吗？`, '提示', { type: 'warning' })
        .then(async () => {
          await setUserBlacklist(row.openid, !row.blacklisted)
          this.$message.success('操作成功')
          await this.loadUsers()
          if (this.currentUser && this.currentUser.openid === row.openid) {
            this.currentUser = await fetchUserDetail(row.openid)
          }
        })
        .catch(() => {})
    },
    handleDepositDeduct(row) {
      if (this.isDepositDisabled(row)) {
        this.$message.warning('保证金功能已关闭，当前不能扣除保证金')
        return
      }
      if (row && row.depositExempt) {
        this.$message.warning('当前用户已获保证金豁免，不能扣除保证金')
        return
      }
      const currentBalanceFen = Math.max(0, Number(row.depositBalanceFen) || 0)
      const currentBalanceYuan = (currentBalanceFen / 100).toFixed(2)
      this.$prompt('请输入扣除金额（元）', '扣保证金', {
        inputValue: '',
        inputPlaceholder: `当前可扣 ${currentBalanceYuan} 元`,
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        inputValidator: (value) => {
          const normalized = String(value || '').trim()
          if (!/^(\d+)(\.\d{1,2})?$/.test(normalized)) {
            return false
          }
          const amountFen = Math.round(Number(normalized) * 100)
          return amountFen > 0 && amountFen <= currentBalanceFen
        },
        inputErrorMessage: '请输入大于 0 且不超过当前保证金余额的金额，最多两位小数'
      })
        .then(async ({ value }) => {
          const amountFen = Math.round(Number(value) * 100)
          await deductUserDeposit(row.openid, amountFen)
          this.$message.success('扣除保证金成功')
          await this.loadUsers()
          if (this.currentUser && this.currentUser.openid === row.openid) {
            this.currentUser = await fetchUserDetail(row.openid)
          }
        })
        .catch((error) => {
          if (error === 'cancel' || error === 'close') {
            return
          }
          this.$message.error(error.message || '扣除保证金失败')
        })
    }
  }
}
</script>

<style scoped>
.user-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.list-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.u-name {
  font-size: 14px;
  font-weight: 600;
}

.u-id {
  margin-top: 2px;
  color: #7d88a3;
  font-size: 12px;
}

.detail-wrap {
  padding: 0 8px;
}

.detail-avatar {
  width: 88px;
  height: 88px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
}

.deposit-amount-ok {
  color: #15803d;
  font-weight: 600;
}

.deposit-amount-empty {
  color: #94a3b8;
}

.deposit-amount-disabled {
  color: #64748b;
  font-weight: 600;
}

.deposit-amount-exempt {
  color: #2563eb;
  font-weight: 600;
}
</style>
